import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface ContactMethod {
  icon: string;
  title: string;
  details: string[];
  action?: {
    label: string;
    href: string;
  };
}

const ContactInfo = () => {
  const contactMethods: ContactMethod[] = [
    {
      icon: 'PhoneIcon',
      title: 'Phone',
      details: ['+44 20 7123 4567', 'Mon-Fri, 9:00-17:00 GMT'],
      action: {
        label: 'Call Now',
        href: 'tel:+442071234567'
      }
    },
    {
      icon: 'EnvelopeIcon',
      title: 'Email',
      details: ['hello@aichangeleaders.com', 'Response within 24 hours'],
      action: {
        label: 'Send Email',
        href: 'mailto:hello@aichangeleaders.com'
      }
    },
    {
      icon: 'MapPinIcon',
      title: 'Office',
      details: ['123 Innovation Street', 'London EC2A 4NE', 'United Kingdom']
    },
    {
      icon: 'ClockIcon',
      title: 'Business Hours',
      details: ['Monday - Friday', '09:00 - 17:00 GMT', 'Closed weekends & bank holidays']
    }
  ];

  const socialLinks = [
    { icon: 'GlobeAltIcon', label: 'LinkedIn', href: 'https://linkedin.com' },
    { icon: 'ChatBubbleLeftRightIcon', label: 'Twitter', href: 'https://twitter.com' },
    { icon: 'VideoCameraIcon', label: 'YouTube', href: 'https://youtube.com' }
  ];

  return (
    <div className="space-y-8">
      <div className="bg-card rounded-lg shadow-card p-8">
        <h3 className="text-2xl font-bold font-headline text-primary mb-6">
          Contact Information
        </h3>
        <div className="space-y-6">
          {contactMethods.map((method, index) => (
            <div key={index} className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                <Icon name={method.icon as any} size={24} variant="outline" className="text-primary" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold font-headline text-foreground mb-2">
                  {method.title}
                </h4>
                {method.details.map((detail, idx) => (
                  <p key={idx} className="text-sm text-muted-foreground font-body">
                    {detail}
                  </p>
                ))}
                {method.action && (
                  <a
                    href={method.action.href}
                    className="inline-flex items-center gap-2 mt-3 text-sm font-semibold text-secondary hover:text-secondary/80 transition-colors duration-300"
                  >
                    {method.action.label}
                    <Icon name="ArrowRightIcon" size={16} variant="outline" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-card rounded-lg shadow-card p-8">
        <h3 className="text-xl font-bold font-headline text-primary mb-4">
          Connect With Us
        </h3>
        <p className="text-sm text-muted-foreground font-body mb-6">
          Follow us on social media for insights, updates, and thought leadership on AI transformation.
        </p>
        <div className="flex flex-wrap gap-3">
          {socialLinks.map((link, index) => (
            <a
              key={index}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 rounded-md border border-input bg-background hover:bg-muted transition-all duration-300"
              aria-label={link.label}
            >
              <Icon name={link.icon as any} size={20} variant="outline" className="text-primary" />
              <span className="text-sm font-body text-foreground">{link.label}</span>
            </a>
          ))}
        </div>
      </div>

      <div className="bg-gradient-to-br from-primary to-secondary rounded-lg shadow-card p-8 text-white">
        <div className="flex items-start gap-4 mb-6">
          <div className="flex-shrink-0 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
            <Icon name="ShieldCheckIcon" size={24} variant="outline" />
          </div>
          <div>
            <h3 className="text-xl font-bold font-headline mb-2">
              Your Privacy Matters
            </h3>
            <p className="text-sm text-white/90 font-body">
              We take data protection seriously. All information submitted through our contact forms is processed in accordance with GDPR regulations and stored securely.
            </p>
          </div>
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-white/80" />
            <span className="text-sm font-body">SSL encrypted transmission</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-white/80" />
            <span className="text-sm font-body">GDPR compliant processing</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-white/80" />
            <span className="text-sm font-body">No data sharing with third parties</span>
          </div>
        </div>
      </div>

      <div className="bg-card rounded-lg shadow-card overflow-hidden">
        <div className="h-64 w-full">
          <iframe
            width="100%"
            height="100%"
            loading="lazy"
            title="AI Change Leaders Office Location"
            referrerPolicy="no-referrer-when-downgrade"
            src="https://www.google.com/maps?q=51.5074,-0.1278&z=14&output=embed"
            className="border-0"
          />
        </div>
      </div>
    </div>
  );
};

export default ContactInfo;